import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Users, Search, RefreshCw, Loader2, ArrowLeft, Upload, CheckCircle2,
  XCircle, ArrowRight, Building2, Mail, Phone, FileText, StickyNote, Clock, Sparkles, RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const COLUMNS = [
  { key: "contact", label: "Contact", sub: "Captured / imported", dot: "bg-slate-400", head: "border-slate-300" },
  { key: "lead", label: "Lead", sub: "Engaging with us", dot: "bg-blue-500", head: "border-blue-400" },
  { key: "qualified", label: "Qualified", sub: "Requirement + design asked", dot: "bg-emerald-500", head: "border-emerald-400" },
  { key: "disqualified", label: "Disqualified", sub: "Not a fit", dot: "bg-rose-400", head: "border-rose-300" },
];

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d) ? String(s) : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function CRMPipeline({ user, onLogout }) {
  const nav = useNavigate();
  const [userPermissions, setUserPermissions] = useState([]);
  const can = (p) => userPermissions.includes(p) || user?.role === "ADMIN";

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  const [selected, setSelected] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [qualifyFor, setQualifyFor] = useState(null);
  const [disqualifyFor, setDisqualifyFor] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const draggedRef = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const handleDrop = (targetStage) => {
    const lead = draggedRef.current;
    draggedRef.current = null;
    setDraggedLead(null);
    setDragOverCol(null);
    if (!lead || (lead.lead_stage || "contact") === targetStage) return;
    if (targetStage === "qualified") {
      if (!can("lead.qualify")) { toast.error("No permission to qualify"); return; }
      setQualifyFor(lead);
    } else if (targetStage === "disqualified") {
      if (!can("lead.disqualify")) { toast.error("No permission to disqualify"); return; }
      setDisqualifyFor(lead);
    } else {
      if (!can("lead.edit")) { toast.error("No permission to move"); return; }
      moveStage(lead, targetStage);
    }
  };

  const sourceName = useCallback(
    (id) => sources.find((s) => s.id === id)?.name || "Unassigned",
    [sources]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (sourceFilter !== "all") params.source_category = sourceFilter;
      const [l, s] = await Promise.all([
        axios.get(`${API}/crm/leads`, { ...authHeaders(), params }).then((r) => r.data),
        axios.get(`${API}/crm/stats`, authHeaders()).then((r) => r.data),
      ]);
      setItems(l.items || []);
      setStats(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load CRM pipeline");
    } finally {
      setLoading(false);
    }
  }, [q, sourceFilter]);

  useEffect(() => {
    axios.get(`${API}/auth/me/permissions`, authHeaders())
      .then((r) => setUserPermissions(r.data.effective_permissions || []))
      .catch(() => {});
    axios.get(`${API}/crm/sources`, authHeaders()).then((r) => setSources(r.data.items || [])).catch(() => {});
    axios.get(`${API}/crm/disqualify-reasons`, authHeaders()).then((r) => setReasons(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = { contact: [], lead: [], qualified: [], disqualified: [] };
    items.forEach((it) => { (g[it.lead_stage || "contact"] || g.contact).push(it); });
    return g;
  }, [items]);

  const moveStage = async (lead, stage) => {
    try {
      await axios.patch(`${API}/crm/leads/${lead.id}/stage`, { stage }, authHeaders());
      toast.success(`Moved ${lead.name} to ${stage}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not move"); }
  };

  const [mapping, setMapping] = useState(false);
  const mapSources = async () => {
    setMapping(true);
    try {
      const r = await axios.post(`${API}/crm/backfill-sources`, {}, authHeaders());
      toast.success(`Mapped ${r.data.sources_mapped} contact(s) to their source`);
      axios.get(`${API}/crm/sources`, authHeaders()).then((res) => setSources(res.data.items || [])).catch(() => {});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not map sources"); }
    finally { setMapping(false); }
  };

  const assignSource = async (lead, sourceId) => {
    try {
      await axios.patch(`${API}/crm/leads/${lead.id}/source`, { source_id: sourceId || null }, authHeaders());
      toast.success("Source updated");
      setSelected((prev) => (prev && prev.id === lead.id ? { ...prev, source_category: sourceId || null } : prev));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update source"); }
  };

  // ---- Bulk selection (single stage column at a time) ----
  const [sel, setSel] = useState({});
  const [selCol, setSelCol] = useState(null);
  const selectedIds = Object.keys(sel);

  const toggleSelect = (lead, colKey) => {
    setSel((prev) => {
      if (selCol && selCol !== colKey) return { [lead.id]: true };
      const next = { ...prev };
      if (next[lead.id]) delete next[lead.id]; else next[lead.id] = true;
      return next;
    });
    setSelCol(colKey);
  };

  const selectAllInColumn = (colKey, leads) => {
    const allSelected = leads.length > 0 && selCol === colKey && leads.every((l) => sel[l.id]);
    if (allSelected) { setSel({}); setSelCol(null); return; }
    const next = {};
    leads.forEach((l) => { next[l.id] = true; });
    setSel(next); setSelCol(colKey);
  };

  const clearSel = () => { setSel({}); setSelCol(null); };

  const bulkMove = async (target) => {
    const ids = Object.keys(sel);
    if (!ids.length) return;
    try {
      const r = await axios.post(`${API}/crm/leads/bulk-stage`, { ids, stage: target }, authHeaders());
      toast.success(`Moved ${r.data.moved} contact(s) to ${target}`);
      clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not move"); }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="crm-pipeline-page">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <div className="bg-zinc-900 text-white">
        <div className="max-w-[1500px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => nav("/")} className="text-white hover:bg-white/10" data-testid="crm-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" /> CRM · Pipeline
              </h1>
              <p className="text-xs text-zinc-400">Contact → Lead → Qualified</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {can("lead.import") && (
              <Button onClick={mapSources} disabled={mapping} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="crm-map-sources-btn">
                {mapping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Map sources
              </Button>
            )}
            {can("lead.import") && (
              <Button onClick={() => setImportOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="crm-import-btn">
                <Upload className="w-4 h-4 mr-2" /> Import Excel
              </Button>
            )}
            <Button variant="outline" size="icon" onClick={load} className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="crm-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* KPIs + filters */}
      <div className="max-w-[1500px] mx-auto px-6 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          {[
            { k: "contact", label: "Contacts" },
            { k: "lead", label: "Leads" },
            { k: "qualified", label: "Qualified" },
            { k: "disqualified", label: "Disqualified" },
          ].map((c) => (
            <div key={c.k} className="bg-white rounded-xl border p-3" data-testid={`crm-kpi-${c.k}`}>
              <div className="text-2xl font-bold text-zinc-800">{stats?.totals?.[c.k] ?? "—"}</div>
              <div className="text-xs text-zinc-500">{c.label}</div>
            </div>
          ))}
          <div className="bg-white rounded-xl border p-3" data-testid="crm-kpi-rate">
            <div className="text-2xl font-bold text-emerald-600">{stats ? `${Math.round((stats.qualification_rate || 0) * 100)}%` : "—"}</div>
            <div className="text-xs text-zinc-500">Qualification rate</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, company, email, phone…"
              className="pl-9"
              data-testid="crm-search-input"
            />
          </div>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[300px]" data-testid="crm-source-filter">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="unassigned" data-testid="crm-source-opt-unassigned">Unassigned (app captures)</SelectItem>
              {sources.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`crm-source-opt-${s.id}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk action bar */}
        {selectedIds.length > 0 && can("lead.edit") && (
          <div className="flex items-center gap-3 mb-3 bg-zinc-900 text-white rounded-xl px-4 py-2.5" data-testid="crm-bulk-bar">
            <span className="text-sm font-medium" data-testid="crm-bulk-count">{selectedIds.length} selected</span>
            {selCol === "contact" && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => bulkMove("lead")} data-testid="crm-bulk-tolead">
                <ArrowRight className="w-3.5 h-3.5 mr-1" /> Move to Lead
              </Button>
            )}
            {selCol === "lead" && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => bulkMove("contact")} data-testid="crm-bulk-tocontact">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Move to Contact
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-zinc-300 hover:bg-white/10 ml-auto" onClick={clearSel} data-testid="crm-bulk-clear">Clear</Button>
          </div>
        )}

        {/* Kanban */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {COLUMNS.map((col) => (
              <div key={col.key} className="flex flex-col" data-testid={`crm-col-${col.key}`}>
                <div className={`flex items-center justify-between px-3 py-2 bg-white rounded-t-xl border border-b-2 ${col.head}`}>
                  <div className="flex items-center gap-2">
                    {can("lead.edit") && (col.key === "contact" || col.key === "lead") && grouped[col.key].length > 0 && (
                      <Checkbox
                        checked={selCol === col.key && grouped[col.key].every((l) => sel[l.id])}
                        onCheckedChange={() => selectAllInColumn(col.key, grouped[col.key])}
                        data-testid={`crm-selectall-${col.key}`}
                      />
                    )}
                    <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                    <div>
                      <div className="font-semibold text-sm text-zinc-800">{col.label}</div>
                      <div className="text-[10px] text-zinc-400">{col.sub}</div>
                    </div>
                  </div>
                  <Badge variant="secondary" data-testid={`crm-count-${col.key}`}>{grouped[col.key].length}</Badge>
                </div>
                <div
                  className={`bg-zinc-100/60 rounded-b-xl border border-t-0 p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-330px)] overflow-y-auto transition-colors ${dragOverCol === col.key ? "ring-2 ring-inset ring-blue-400 bg-blue-50/60" : ""}`}
                  onDragOver={(e) => { if (draggedRef.current) { e.preventDefault(); setDragOverCol(col.key); } }}
                  onDragLeave={() => setDragOverCol((c) => (c === col.key ? null : c))}
                  onDrop={(e) => { e.preventDefault(); handleDrop(col.key); }}
                  data-testid={`crm-dropzone-${col.key}`}
                >
                  {grouped[col.key].length === 0 && (
                    <div className="text-center text-xs text-zinc-400 py-6">{dragOverCol === col.key ? "Drop here" : "No contacts"}</div>
                  )}
                  {grouped[col.key].map((it) => (
                    <LeadCard
                      key={it.id}
                      lead={it}
                      sourceName={sourceName}
                      can={can}
                      selectable={can("lead.edit") && (col.key === "contact" || col.key === "lead")}
                      checked={!!sel[it.id]}
                      onToggleSelect={() => toggleSelect(it, col.key)}
                      onOpen={() => setSelected(it)}
                      onMove={moveStage}
                      onQualify={() => setQualifyFor(it)}
                      onDisqualify={() => setDisqualifyFor(it)}
                      onDragStart={() => { draggedRef.current = it; setDraggedLead(it); }}
                      onDragEnd={() => { draggedRef.current = null; setDraggedLead(null); setDragOverCol(null); }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          can={can}
          sourceName={sourceName}
          sources={sources}
          onAssignSource={assignSource}
          onClose={() => setSelected(null)}
          onChanged={() => { load(); setSelected(null); }}
          onQualify={() => { setQualifyFor(selected); }}
          onDisqualify={() => { setDisqualifyFor(selected); }}
          onMove={moveStage}
        />
      )}

      {importOpen && (
        <ImportDialog sources={sources} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />
      )}

      {qualifyFor && (
        <QualifyDialog lead={qualifyFor} onClose={() => setQualifyFor(null)} onDone={() => { setQualifyFor(null); setSelected(null); load(); }} />
      )}

      {disqualifyFor && (
        <DisqualifyDialog lead={disqualifyFor} reasons={reasons} onClose={() => setDisqualifyFor(null)} onDone={() => { setDisqualifyFor(null); setSelected(null); load(); }} />
      )}
    </div>
  );
}

function LeadCard({ lead, sourceName, can, selectable, checked, onToggleSelect, onOpen, onMove, onQualify, onDisqualify, onDragStart, onDragEnd }) {
  const stage = lead.lead_stage || "contact";
  const draggable = can("lead.edit") || can("lead.qualify") || can("lead.disqualify");
  return (
    <div
      className={`relative bg-white rounded-lg border p-3 hover:shadow-md transition-shadow cursor-pointer ${draggable ? "active:cursor-grabbing" : ""} ${checked ? "ring-2 ring-blue-400 border-blue-300" : ""}`}
      onClick={onOpen}
      draggable={draggable}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart?.(); }}
      onDragEnd={() => onDragEnd?.()}
      data-testid={`crm-card-${lead.id}`}
    >
      {selectable && (
        <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={!!checked} onCheckedChange={() => onToggleSelect?.()} data-testid={`crm-select-${lead.id}`} />
        </div>
      )}
      <div className={`font-medium text-sm text-zinc-800 truncate ${selectable ? "pr-6" : ""}`}>{lead.company || "(no company)"}</div>
      <div className="text-xs text-zinc-500 truncate">{lead.name}{lead.designation ? ` · ${lead.designation}` : ""}</div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <Badge variant="outline" className="text-[10px] py-0 max-w-[140px] truncate">{sourceName(lead.source_category)}</Badge>
        {lead.requirement === "yes" && <Badge className="text-[10px] py-0 bg-amber-100 text-amber-700 border-amber-200">Requirement</Badge>}
        {lead.enquiry_id && <Badge className="text-[10px] py-0 bg-emerald-100 text-emerald-700 border-emerald-200">{lead.enquiry_id}</Badge>}
      </div>
      {stage !== "qualified" && stage !== "disqualified" && (
        <div className="flex items-center gap-1.5 mt-2.5" onClick={(e) => e.stopPropagation()}>
          {stage === "contact" && can("lead.edit") && (
            <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => onMove(lead, "lead")} data-testid={`crm-tolead-${lead.id}`}>
              <ArrowRight className="w-3 h-3 mr-1" /> Lead
            </Button>
          )}
          {can("lead.qualify") && (
            <Button size="sm" className="h-7 text-xs flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={onQualify} data-testid={`crm-qualify-${lead.id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Qualify
            </Button>
          )}
          {can("lead.disqualify") && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600 hover:bg-rose-50 px-2" onClick={onDisqualify} data-testid={`crm-disqualify-${lead.id}`}>
              <XCircle className="w-3 h-3" />
            </Button>
          )}
        </div>
      )}
      {stage === "disqualified" && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          {lead.disqualified_reason && (
            <div className="text-[11px] text-rose-500">
              Reason: {lead.disqualified_reason}
              {lead.disqualified_comment ? <span className="text-zinc-400"> — {lead.disqualified_comment}</span> : null}
            </div>
          )}
          {can("lead.edit") && (
            <Button size="sm" variant="outline" className="h-7 text-xs mt-2 w-full" onClick={() => onMove(lead, "contact")} data-testid={`crm-reopen-${lead.id}`}>
              <RotateCcw className="w-3 h-3 mr-1" /> Reopen
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function LeadDrawer({ lead, can, sourceName, sources = [], onAssignSource, onClose, onChanged, onQualify, onDisqualify, onMove }) {
  const [activities, setActivities] = useState([]);
  const [emails, setEmails] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    axios.get(`${API}/crm/leads/${lead.id}/activities`, authHeaders()).then((r) => setActivities(r.data.items || [])).catch(() => {});
    if (can("lead.email")) {
      axios.get(`${API}/crm/leads/${lead.id}/emails`, authHeaders()).then((r) => setEmails(r.data.items || [])).catch(() => {});
    }
  }, [lead.id]);
  useEffect(() => { reload(); }, [reload]);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/crm/leads/${lead.id}/notes`, { text: note }, authHeaders());
      setNote(""); reload();
    } catch (e) { toast.error("Could not add note"); } finally { setBusy(false); }
  };

  const stage = lead.lead_stage || "contact";

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="crm-lead-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Building2 className="w-4 h-4" /> {lead.company || "(no company)"}</SheetTitle>
        </SheetHeader>
        <div className="py-3 space-y-1 text-sm">
          <div className="font-medium text-zinc-800">{lead.name} {lead.designation ? <span className="text-zinc-400">· {lead.designation}</span> : null}</div>
          {lead.email && <div className="flex items-center gap-2 text-zinc-600"><Mail className="w-3.5 h-3.5" /> {lead.email}</div>}
          {lead.phone && <div className="flex items-center gap-2 text-zinc-600"><Phone className="w-3.5 h-3.5" /> {lead.phone}</div>}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <Badge variant="outline">{sourceName(lead.source_category)}</Badge>
            <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 capitalize">{stage}</Badge>
            {lead.enquiry_id && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{lead.enquiry_id}</Badge>}
          </div>
          {can("lead.edit") && (
            <div className="pt-2">
              <label className="text-[11px] font-medium text-zinc-500">Lead source</label>
              <Select
                value={lead.source_category || "unassigned"}
                onValueChange={(v) => onAssignSource?.(lead, v === "unassigned" ? null : v)}
              >
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="crm-drawer-source-select">
                  <SelectValue placeholder="Assign a source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {sources.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`crm-drawer-source-opt-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {lead.requirement_text && <div className="text-zinc-600 pt-1"><b>Requirement:</b> {lead.requirement_text}</div>}
        </div>

        {/* Actions */}
        {stage !== "qualified" && stage !== "disqualified" && (
          <div className="flex gap-2 py-2 border-y">
            {stage === "contact" && can("lead.edit") && (
              <Button size="sm" variant="outline" className="flex-1" onClick={() => onMove(lead, "lead")} data-testid="crm-drawer-tolead">Mark as Lead</Button>
            )}
            {can("lead.qualify") && <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={onQualify} data-testid="crm-drawer-qualify">Qualify</Button>}
            {can("lead.disqualify") && <Button size="sm" variant="outline" className="text-rose-600" onClick={onDisqualify} data-testid="crm-drawer-disqualify">Disqualify</Button>}
          </div>
        )}
        {stage === "disqualified" && (
          <div className="py-2 border-y space-y-2">
            {lead.disqualified_reason && (
              <div className="text-xs text-rose-500">
                <b>Reason:</b> {lead.disqualified_reason}
                {lead.disqualified_comment ? <div className="text-zinc-500 mt-0.5">{lead.disqualified_comment}</div> : null}
              </div>
            )}
            {can("lead.edit") && (
              <Button size="sm" variant="outline" className="w-full" onClick={() => onMove(lead, "contact")} data-testid="crm-drawer-reopen">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen lead
              </Button>
            )}
          </div>
        )}

        <Tabs defaultValue="activity" className="mt-3">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="activity" data-testid="crm-tab-activity"><StickyNote className="w-3.5 h-3.5 mr-1" /> Activity</TabsTrigger>
            <TabsTrigger value="emails" data-testid="crm-tab-emails"><Mail className="w-3.5 h-3.5 mr-1" /> Emails ({emails.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="activity" className="space-y-3">
            {can("lead.edit") && (
              <div className="flex gap-2">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="text-sm min-h-[40px]" data-testid="crm-note-input" />
                <Button size="sm" onClick={addNote} disabled={busy} data-testid="crm-note-add">Add</Button>
              </div>
            )}
            <div className="space-y-2">
              {activities.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">No activity yet</div>}
              {activities.map((a) => (
                <div key={a.id} className="flex gap-2 text-xs border-l-2 border-zinc-200 pl-2 py-0.5">
                  <Clock className="w-3 h-3 text-zinc-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-zinc-700">{a.text}</div>
                    <div className="text-zinc-400">{a.by_name} · {fmtDate(a.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="emails" className="space-y-2">
            {!can("lead.email") && <div className="text-xs text-zinc-400 py-4 text-center">No permission to view emails</div>}
            {can("lead.email") && emails.length === 0 && (
              <div className="text-xs text-zinc-400 py-4 text-center flex flex-col items-center gap-1">
                <Mail className="w-5 h-5 text-zinc-300" />
                No emails linked to this contact yet.
                <span className="text-[10px]">Emails sent/received via the synced mailbox will appear here automatically.</span>
              </div>
            )}
            {emails.map((e) => (
              <div key={e.id} className="border rounded-lg p-2 text-xs" data-testid="crm-email-item">
                <div className="font-medium text-zinc-800 truncate">{e.subject || "(no subject)"}</div>
                <div className="text-zinc-500 truncate">{e.from_address?.email} · {fmtDate(e.received_at || e.date)}</div>
                <div className="text-zinc-600 line-clamp-2 mt-1">{(e.body_text || e.snippet || "").slice(0, 160)}</div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function QualifyDialog({ lead, onClose, onDone }) {
  const [req, setReq] = useState(lead.requirement_text || "");
  const [design, setDesign] = useState(lead.design_request_text || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!req.trim()) { toast.error("Requirement is needed to qualify"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/crm/leads/${lead.id}/qualify`, { requirement_text: req, design_request_text: design }, authHeaders());
      toast.success(`Qualified — ${r.data.enquiry_id} created`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not qualify"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="crm-qualify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600" /> Qualify — {lead.company || lead.name}</DialogTitle>
          <DialogDescription>Capture the specific requirement and design request. This creates a linked Enquiry/Opportunity.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">Requirement *</label>
            <Textarea value={req} onChange={(e) => setReq(e.target.value)} placeholder="What does the customer need?" data-testid="crm-qualify-req" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Design request</label>
            <Textarea value={design} onChange={(e) => setDesign(e.target.value)} placeholder="What design / drawing did they ask for?" data-testid="crm-qualify-design" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="crm-qualify-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Qualify & create enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisqualifyDialog({ lead, reasons, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/crm/leads/${lead.id}/disqualify`, { reason, comment }, authHeaders());
      toast.success("Disqualified");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not disqualify"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="crm-disqualify-dialog">
        <DialogHeader>
          <DialogTitle>Disqualify — {lead.company || lead.name}</DialogTitle>
          <DialogDescription>Choose why this lead isn't a fit. You can add an optional comment for context.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">Reason *</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="crm-disqualify-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {reasons.map((r) => <SelectItem key={r.id} value={r.name} data-testid={`crm-reason-${r.id}`}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Comment (optional)</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add any context for disqualifying this lead…"
              data-testid="crm-disqualify-comment"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} variant="destructive" data-testid="crm-disqualify-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disqualify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ sources, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [sourceId, setSourceId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    if (!file) { toast.error("Choose an Excel file"); return; }
    if (!sourceId) { toast.error("Pick a source"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_id", sourceId);
      fd.append("dry_run", dryRun ? "true" : "false");
      const r = await axios.post(`${API}/crm/import`, fd, { ...authHeaders() });
      if (dryRun) {
        setPreview(r.data);
      } else {
        toast.success(`Imported ${r.data.imported} contacts (${r.data.duplicates} duplicates skipped)`);
        onDone();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="crm-import-dialog">
        <DialogHeader>
          <DialogTitle>Import leads from Excel</DialogTitle>
          <DialogDescription>Reads the "Lead Manager Contacts" and "BizConnect" contact sheets. Duplicates (by email/phone) are skipped.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="crm-import-file" />
          <Select value={sourceId} onValueChange={(v) => { setSourceId(v); setPreview(null); }}>
            <SelectTrigger data-testid="crm-import-source"><SelectValue placeholder="Tag these leads with source…" /></SelectTrigger>
            <SelectContent>
              {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {preview && (
            <div className="bg-zinc-50 border rounded-lg p-3 text-sm" data-testid="crm-import-preview">
              <div className="flex gap-4 mb-2">
                <span><b>{preview.parsed}</b> rows</span>
                <span className="text-emerald-600"><b>{preview.to_import}</b> to import</span>
                <span className="text-amber-600"><b>{preview.duplicates}</b> duplicates</span>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {(preview.preview || []).slice(0, 20).map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs border-b py-1">
                    <span className="truncate">{r.company || r.name} <span className="text-zinc-400">· {r.lead_stage}</span></span>
                    <span className={r._status?.includes("skip") ? "text-amber-600" : "text-emerald-600"}>{r._status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!preview ? (
            <Button onClick={() => run(true)} disabled={busy} data-testid="crm-import-preview-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
            </Button>
          ) : (
            <Button onClick={() => run(false)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="crm-import-commit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Import ${preview.to_import} contacts`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
